import {
  type KeyboardEvent,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Box,
  Container,
  Header,
  SpaceBetween,
  Spinner
} from '@cloudscape-design/components';
import axios from 'axios';
import {
  MessageSquarePlus,
  MoreVertical,
  Pencil,
  SendHorizontal,
  Trash2
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { UserContext } from '../../../context/authContext';
import { useAlert } from '../../../context/alertContext';
import { DocumentContext } from '../../../context/documentContext';
import { useUnsavedChangesWarning } from '../rfpUploader/useUnsavedChangesWarning';
import { ScrollDownArrow } from '../shared/scrollDownArrow';
import {
  buildNotesDocxBlob,
  type NoteEntry,
  type ParagraphSection
} from './createAnalysisDocument';

interface Evaluation5EBasis {
  benchmark_summary?: string;
  evaluated_signal_summary?: string;
  reason?: string;
}

interface Evaluation5EDimensionAssessment {
  benchmark_dimension?: string;
  evaluated_dimension?: string;
  dimension_match?: string;
}

interface Evaluation5EMechanicAssessment {
  benchmark_primary_comparison_mechanic?: string;
  evaluated_primary_comparison_mechanic?: string;
  mechanic_match?: string;
}

interface Evaluation5EStructureAssessment {
  benchmark_expected_value_structure_type?: string;
  evaluated_observed_value_structure_type?: string;
  structure_match?: string;
}

interface Evaluation5EEvidence {
  benchmark_source_context_id?: string;
  benchmark_source_text?: string;
  evaluated_context_id?: string;
  evaluated_sentence_ids?: string[];
  evaluated_source_text?: string;
}

interface Evaluation5E {
  comparison_result?: string;
  confidence?: string;
  basis?: Evaluation5EBasis;
  dimension_assessment?: Evaluation5EDimensionAssessment;
  mechanic_assessment?: Evaluation5EMechanicAssessment;
  structure_assessment?: Evaluation5EStructureAssessment;
  evidence?: Evaluation5EEvidence;
  watchpoints?: string[];
}

interface CandidateEvaluation {
  candidateId: string;
  evaluation: Evaluation5E;
  cardKey?: string;
  includeInCompanionDocument?: boolean;
  sourcePath?: JsonPathSegment[];
}

interface OutOfScope {
  context_id?: string;
  include_in_companion_document?: boolean;
  includeInCompanionDocument?: boolean;
  reason?: string;
  sentence_Ids?: string[];
  sentence_id?: string;
  sentence_ids?: string[];
  signal_id?: string;
  sourcePath?: JsonPathSegment[];
  sourceText?: string;
  out_of_scope_id?: string;
  source_text?: string;
}

interface CandidateNote {
  id: string;
  noteDocumentId: string;
  textId: string;
  text: string;
  owner: string;
  userId: string;
  date: number;
}

interface SentenceDocumentOrder {
  minY: number;
  pageNumber: number;
  sentenceIndex: number;
}

interface CompanionDocumentEntry {
  comparisonColor?: string;
  comparisonLabel?: string;
  notes: NoteEntry[];
  originalIndex: number;
  pageNumber: number | string;
  sortMinY: number;
  sortPage: number;
  sortSentenceIndex: number;
  text: string;
}

type JsonPathSegment = string | number;

type AnalyzedContractNoteTargetType = 'candidate' | 'out_of_scope';

interface AnalyzedContractNoteTarget {
  type: AnalyzedContractNoteTargetType;
  id?: string;
  candidateId?: string;
  candidate_id?: string;
  outOfScopeId?: string;
  outOfScopeID?: string;
  out_of_scope_id?: string;
  out_of_scode_id?: string;
  signal_id?: string;
  context_id?: string;
  sentenceIds?: string[];
  sentence_ids?: string[];
}

interface AnalyzedContractNoteTextUser {
  _id?: string;
  sub?: string;
  name?: string;
  email?: string;
}

interface AnalyzedContractNoteText {
  _id?: string;
  text: string;
  userId?: string | AnalyzedContractNoteTextUser;
  capturedAt?: string | number | Date;
}

interface AnalyzedContractNoteDocument {
  _id: string;
  customerId: string;
  documentId: string;
  target: AnalyzedContractNoteTarget;
  texts: AnalyzedContractNoteText[];
  createdAt?: string;
  updatedAt?: string;
}

interface CreateAnalyzedContractNoteRequest {
  documentId: string;
  target: AnalyzedContractNoteTarget;
  texts: Array<Pick<AnalyzedContractNoteText, 'text'>>;
}

interface CreateCandidateNotePayload {
  documentId: string;
  noteText: string;
  owner: string;
  target: AnalyzedContractNoteTarget;
}

interface UpdateCandidateNotePayload {
  documentId: string;
  noteText_id: string;
  noteId: string;
  text: string;
}

interface DeleteCandidateNotePayload {
  documentId: string;
  _id: string;
  textId: string;
}

const COMPARISON_RESULT_OPTIONS = [
  'stronger_than_benchmark',
  'aligned_with_benchmark',
  'weaker_than_benchmark',
  'missing_benchmarked_element',
  'unclear_or_not_safely_comparable',
  'not_applicable'
] as const;
const NOTES_FILTER = 'notes';
const INCLUDED_SUMMARY_FILTER = 'included_in_summary';
const SAVE_ANALYZED_CONTRACT_TYPE = 'saveAnalyzedContract';
const INCLUDE_IN_COMPANION_DOCUMENT_FIELD = 'include_in_companion_document';
const RESULT_FILTER_OPTIONS = [
  ...COMPARISON_RESULT_OPTIONS,
  NOTES_FILTER,
  INCLUDED_SUMMARY_FILTER
] as const;

const COMPARISON_NORMALIZED_TEXT: {
  [key: string]: { text: string; color: string };
} = {
  stronger_than_benchmark: { text: 'Not Favorable', color: 'C00000' },
  aligned_with_benchmark: {
    text: 'Favorable and As Expected',
    color: ''
  },
  weaker_than_benchmark: { text: 'Favorable', color: '' },
  missing_benchmarked_element: { text: 'Not Addressed', color: '' },
  unclear_or_not_safely_comparable: {
    text: 'Measured Differently',
    color: ''
  },
  not_applicable: { text: 'Out of Scope', color: '' }
};

type ComparisonResultFilter = (typeof COMPARISON_RESULT_OPTIONS)[number];
type NotesFilter = typeof NOTES_FILTER;
type IncludedSummaryFilter = typeof INCLUDED_SUMMARY_FILTER;
type AnalysisFilter =
  | ComparisonResultFilter
  | NotesFilter
  | IncludedSummaryFilter;

const isComparisonResultFilter = (
  value: string | undefined
): value is ComparisonResultFilter =>
  !!value &&
  COMPARISON_RESULT_OPTIONS.includes(value as ComparisonResultFilter);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const cloneJsonValue = <T,>(value: T): T =>
  JSON.parse(JSON.stringify(value)) as T;

const toStringValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
};

const toBooleanValue = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  return false;
};

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map((item) => toStringValue(item)).filter(Boolean)
    : [];

const toEvaluation5E = (value: unknown): Evaluation5E | null => {
  if (!isRecord(value)) {
    return null;
  }

  const basis = isRecord(value.basis) ? value.basis : {};
  const dimensionAssessment = isRecord(value.dimension_assessment)
    ? value.dimension_assessment
    : {};
  const mechanicAssessment = isRecord(value.mechanic_assessment)
    ? value.mechanic_assessment
    : {};
  const structureAssessment = isRecord(value.structure_assessment)
    ? value.structure_assessment
    : {};
  const evidence = isRecord(value.evidence) ? value.evidence : {};

  return {
    comparison_result: toStringValue(value.comparison_result),
    confidence: toStringValue(value.confidence),
    basis: {
      benchmark_summary: toStringValue(basis.benchmark_summary),
      evaluated_signal_summary: toStringValue(basis.evaluated_signal_summary),
      reason: toStringValue(basis.reason)
    },
    dimension_assessment: {
      benchmark_dimension: toStringValue(
        dimensionAssessment.benchmark_dimension
      ),
      evaluated_dimension: toStringValue(
        dimensionAssessment.evaluated_dimension
      ),
      dimension_match: toStringValue(dimensionAssessment.dimension_match)
    },
    mechanic_assessment: {
      benchmark_primary_comparison_mechanic: toStringValue(
        mechanicAssessment.benchmark_primary_comparison_mechanic
      ),
      evaluated_primary_comparison_mechanic: toStringValue(
        mechanicAssessment.evaluated_primary_comparison_mechanic
      ),
      mechanic_match: toStringValue(mechanicAssessment.mechanic_match)
    },
    structure_assessment: {
      benchmark_expected_value_structure_type: toStringValue(
        structureAssessment.benchmark_expected_value_structure_type
      ),
      evaluated_observed_value_structure_type: toStringValue(
        structureAssessment.evaluated_observed_value_structure_type
      ),
      structure_match: toStringValue(structureAssessment.structure_match)
    },
    evidence: {
      benchmark_source_context_id: toStringValue(
        evidence.benchmark_source_context_id
      ),
      benchmark_source_text: toStringValue(evidence.benchmark_source_text),
      evaluated_context_id: toStringValue(evidence.evaluated_context_id),
      evaluated_sentence_ids: toStringArray(evidence.evaluated_sentence_ids),
      evaluated_source_text: toStringValue(evidence.evaluated_source_text)
    },
    watchpoints: toStringArray(value.watchpoints)
  };
};

const getIncludeInCompanionDocument = (value: unknown): boolean =>
  isRecord(value)
    ? toBooleanValue(
        value[INCLUDE_IN_COMPANION_DOCUMENT_FIELD] ??
          value.includeInCompanionDocument
      )
    : false;

const extractCandidateEvaluations = (
  value: unknown,
  path: JsonPathSegment[] = []
): CandidateEvaluation[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) =>
      extractCandidateEvaluations(item, [...path, index])
    );
  }

  if (isRecord(value)) {
    const evaluation = toEvaluation5E(value.evaluation_5e);

    if (evaluation) {
      return [
        {
          candidateId:
            toStringValue(value.candidate_id) ||
            `Candidate ${path[path.length - 1] ?? 0}`,
          evaluation,
          includeInCompanionDocument: getIncludeInCompanionDocument(value),
          sourcePath: path
        }
      ];
    }

    return Object.entries(value).flatMap(([key, entry]) =>
      extractCandidateEvaluations(entry, [...path, key])
    );
  }

  return [];
};

const extractOutOfScopeItems = (
  value: unknown,
  pathPrefix: JsonPathSegment[] = []
): OutOfScope[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item, index) => {
      if (!isRecord(item)) {
        return [];
      }

      return [
        {
          ...(item as OutOfScope),
          include_in_companion_document: getIncludeInCompanionDocument(item),
          sourcePath: [...pathPrefix, index]
        }
      ];
    });
};

const buildSummaryInclusionState = ({
  evaluations,
  outOfScopeItems
}: {
  evaluations: CandidateEvaluation[];
  outOfScopeItems: OutOfScope[];
}) =>
  ({
    ...evaluations.reduce(
      (accumulator, candidateEvaluation, index) => {
        const cardKey =
          candidateEvaluation.cardKey ||
          `${candidateEvaluation.candidateId}-${index}`;

        accumulator[cardKey] = !!candidateEvaluation.includeInCompanionDocument;
        return accumulator;
      },
      {} as Record<string, boolean>
    ),
    ...outOfScopeItems.reduce(
      (accumulator, item, index) => {
        const cardKey = getOutOfScopeCardKey(item, index);

        accumulator[cardKey] = !!getIncludeInCompanionDocument(item);
        return accumulator;
      },
      {} as Record<string, boolean>
    )
  }) satisfies Record<string, boolean>;

const areBooleanRecordsEqual = (
  leftRecord: Record<string, boolean>,
  rightRecord: Record<string, boolean>
) => {
  const recordKeys = new Set([
    ...Object.keys(leftRecord),
    ...Object.keys(rightRecord)
  ]);

  return [...recordKeys].every(
    (recordKey) => !!leftRecord[recordKey] === !!rightRecord[recordKey]
  );
};

const updateRecordAtPath = (
  rootValue: unknown,
  path: JsonPathSegment[],
  updateRecord: (record: Record<string, unknown>) => void
) => {
  let currentValue: unknown = rootValue;

  for (const segment of path) {
    if (!Array.isArray(currentValue) && !isRecord(currentValue)) {
      return false;
    }

    currentValue = currentValue[segment as keyof typeof currentValue];
  }

  if (!isRecord(currentValue)) {
    return false;
  }

  updateRecord(currentValue);
  return true;
};

const removeEmbeddingFieldsFromCandidates = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => removeEmbeddingFieldsFromCandidates(item));
  }

  if (!isRecord(value)) {
    return value;
  }

  delete value.embedding;
  delete value.embeddingSourceText;

  Object.keys(value).forEach((key) => {
    value[key] = removeEmbeddingFieldsFromCandidates(value[key]);
  });

  return value;
};

const renderValue = (value: string | undefined) =>
  value && value.trim() ? value : 'N/A';

const getOutOfScopeCardKey = (item: OutOfScope, index: number): string =>
  item.signal_id ||
  item.out_of_scope_id ||
  item.context_id ||
  `out-of-scope-${index}`;

const getOutOfScopeTargetId = (item: OutOfScope, fallbackKey: string): string =>
  item.out_of_scope_id || item.signal_id || item.context_id || fallbackKey;

const normalizeNoteTargetId = (value: unknown): string =>
  toStringValue(value).trim();

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message || fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const getNoteTargetType = (
  target: AnalyzedContractNoteTarget
): AnalyzedContractNoteTargetType | '' => {
  const targetType = normalizeNoteTargetId(target.type).toLowerCase();

  if (targetType === 'candidate') {
    return 'candidate';
  }

  if (
    targetType === 'out_of_scope' ||
    targetType === 'outofscope' ||
    targetType === 'out-of-scope'
  ) {
    return 'out_of_scope';
  }

  return '';
};

const getNoteTargetCandidateId = (target: AnalyzedContractNoteTarget): string =>
  normalizeNoteTargetId(target.candidateId || target.candidate_id || target.id);

const getNoteTargetOutOfScopeId = (
  target: AnalyzedContractNoteTarget
): string =>
  normalizeNoteTargetId(
    target.outOfScopeId ||
      target.outOfScopeID ||
      target.out_of_scope_id ||
      target.out_of_scode_id ||
      target.signal_id ||
      target.context_id ||
      target.id
  );

const getNoteTargetSentenceIds = (
  target: AnalyzedContractNoteTarget
): string[] =>
  target.sentenceIds?.length
    ? target.sentenceIds
    : target.sentence_ids?.length
      ? target.sentence_ids
      : [];

const getNotesForTarget = ({
  notesByTargetId,
  targetId,
  fallbackTargetIds = []
}: {
  notesByTargetId: Record<string, CandidateNote[]>;
  targetId: string;
  fallbackTargetIds?: Array<string | undefined>;
}): CandidateNote[] => {
  const lookupKeys = [targetId, ...fallbackTargetIds]
    .map((lookupKey) => normalizeNoteTargetId(lookupKey))
    .filter(Boolean);
  const seenNoteIds = new Set<string>();

  return lookupKeys.flatMap((lookupKey) =>
    (notesByTargetId[lookupKey] || []).filter((note) => {
      if (seenNoteIds.has(note.id)) {
        return false;
      }

      seenNoteIds.add(note.id);
      return true;
    })
  );
};

const isAnalyzedContractNoteDocument = (
  value: unknown
): value is AnalyzedContractNoteDocument =>
  isRecord(value) && isRecord(value.target) && Array.isArray(value.texts);

const extractAnalyzedContractNoteDocuments = (
  value: unknown
): AnalyzedContractNoteDocument[] => {
  if (isAnalyzedContractNoteDocument(value)) {
    return [value];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => extractAnalyzedContractNoteDocuments(item));
  }

  if (!isRecord(value)) {
    return [];
  }

  const directCollectionKeys = [
    'result',
    'results',
    'notes',
    'note',
    'data',
    'items',
    'documents',
    'body'
  ];

  for (const key of directCollectionKeys) {
    const nestedValue = value[key];
    const nestedNotes = extractAnalyzedContractNoteDocuments(nestedValue);

    if (nestedNotes.length > 0) {
      return nestedNotes;
    }
  }

  return Object.values(value).flatMap((nestedValue) =>
    extractAnalyzedContractNoteDocuments(nestedValue)
  );
};

const getNoteTextOwner = (
  noteText: AnalyzedContractNoteText,
  fallbackOwner: string
) => {
  if (typeof noteText.userId === 'object' && noteText.userId !== null) {
    return noteText.userId.name || noteText.userId.email || fallbackOwner;
  }

  return fallbackOwner;
};

const getNoteTextUserId = (noteText: AnalyzedContractNoteText): string => {
  if (typeof noteText.userId === 'string') {
    return noteText.userId;
  }

  if (typeof noteText.userId === 'object' && noteText.userId !== null) {
    return noteText.userId._id || noteText.userId.sub || '';
  }

  return '';
};

const getNoteTextDate = (noteText: AnalyzedContractNoteText) => {
  const capturedAt = noteText.capturedAt
    ? new Date(noteText.capturedAt).getTime()
    : Date.now();

  return Number.isFinite(capturedAt) ? capturedAt : Date.now();
};

const toCandidateNote = (
  noteText: AnalyzedContractNoteText,
  fallbackOwner: string,
  noteDocumentId = ''
): CandidateNote => {
  const textId =
    noteText._id || `${noteText.text}-${getNoteTextDate(noteText)}`;

  return {
    id: textId,
    noteDocumentId,
    textId,
    text: noteText.text,
    owner: getNoteTextOwner(noteText, fallbackOwner),
    userId: getNoteTextUserId(noteText),
    date: getNoteTextDate(noteText)
  };
};
const normalizeNoteTarget = (
  target: AnalyzedContractNoteTarget
): AnalyzedContractNoteTarget => {
  const normalizedType = getNoteTargetType(target);

  if (normalizedType === 'candidate') {
    return {
      type: 'candidate',
      candidateId: getNoteTargetCandidateId(target),
      sentenceIds: getNoteTargetSentenceIds(target)
    };
  }

  if (normalizedType === 'out_of_scope') {
    return {
      type: 'out_of_scope',
      outOfScopeId: getNoteTargetOutOfScopeId(target),
      sentenceIds: getNoteTargetSentenceIds(target)
    };
  }

  return target;
};

const extractCreatedCandidateNote = (
  responseData: unknown,
  fallbackText: string,
  fallbackOwner: string
): CandidateNote => {
  const data = isRecord(responseData) ? responseData : {};
  const result = data.result ?? data.note ?? data;
  const resultValue = Array.isArray(result) ? result[0] : result;

  if (isRecord(resultValue) && Array.isArray(resultValue.texts)) {
    const createdText = resultValue.texts[resultValue.texts.length - 1] as
      | AnalyzedContractNoteText
      | undefined;

    if (createdText?.text) {
      return toCandidateNote(
        createdText,
        fallbackOwner,
        toStringValue(resultValue._id)
      );
    }
  }

  if (isRecord(resultValue) && typeof resultValue.text === 'string') {
    return toCandidateNote(
      resultValue as unknown as AnalyzedContractNoteText,
      fallbackOwner
    );
  }

  const fallbackId = `${fallbackText}-${Date.now()}`;

  return {
    id: fallbackId,
    noteDocumentId: '',
    textId: fallbackId,
    text: fallbackText,
    owner: fallbackOwner,
    userId: '',
    date: Date.now()
  };
};

const getOutOfScopeItemSentenceIds = (item: OutOfScope): string[] => {
  if (item?.sentence_Ids && item.sentence_Ids.length > 0) {
    return item.sentence_Ids;
  }

  if (item.sentence_ids?.length) {
    return item.sentence_ids;
  }

  if (item.sentence_id) {
    return [item.sentence_id];
  }

  return [];
};

const buildCandidateNotesByTargetId = ({
  noteDocuments,
  fallbackOwner
}: {
  noteDocuments: AnalyzedContractNoteDocument[];
  fallbackOwner: string;
}): Record<string, CandidateNote[]> => {
  const notesByTargetId: Record<string, CandidateNote[]> = {};

  noteDocuments.forEach((noteDocument) => {
    const targetType = getNoteTargetType(noteDocument.target);
    const targetId =
      targetType === 'candidate'
        ? getNoteTargetCandidateId(noteDocument.target)
        : getNoteTargetOutOfScopeId(noteDocument.target);

    if (!targetType || !targetId) {
      return;
    }

    const notes = noteDocument.texts
      .filter((noteText) => noteText.text?.trim())
      .map((noteText) =>
        toCandidateNote(noteText, fallbackOwner, noteDocument._id)
      );

    notesByTargetId[targetId] = [
      ...(notesByTargetId[targetId] || []),
      ...notes
    ].sort((firstNote, secondNote) => firstNote.date - secondNote.date);
  });

  return notesByTargetId;
};

const createCandidateNote = async (
  payload: CreateCandidateNotePayload,
  auth: { headers: { Authorization: string } }
): Promise<CandidateNote> => {
  if (!auth.headers.Authorization) {
    throw new Error('Missing authorization for note creation.');
  }

  const requestBody: CreateAnalyzedContractNoteRequest = {
    documentId: payload.documentId,
    target: normalizeNoteTarget(payload.target),
    texts: [{ text: payload.noteText }]
  };

  const response = await axios.post<AnalyzedContractNoteDocument>(
    `${process.env.REACT_APP_KORU_API}/analyzed-contract-notes?type=addNew`,
    requestBody,
    auth
  );

  return extractCreatedCandidateNote(
    response.data,
    payload.noteText,
    payload.owner
  );
};

const updateCandidateNoteText = async (
  payload: UpdateCandidateNotePayload,
  auth: { headers: { Authorization: string } }
) => {
  if (!auth.headers.Authorization) {
    throw new Error('Missing authorization for note update.');
  }

  await axios.post(
    `${process.env.REACT_APP_KORU_API}/analyzed-contract-notes?type=update`,
    payload,
    auth
  );
};

const deleteCandidateNoteText = async (
  payload: DeleteCandidateNotePayload,
  auth: { headers: { Authorization: string } }
) => {
  if (!auth.headers.Authorization) {
    throw new Error('Missing authorization for note deletion.');
  }

  await axios.post(
    `${process.env.REACT_APP_KORU_API}/analyzed-contract-notes?type=remove`,
    payload,
    auth
  );
};

const getCandidateNotesForDocument = async ({
  documentId,
  auth,
  fallbackOwner
}: {
  documentId: string;
  auth: { headers: { Authorization: string } };
  fallbackOwner: string;
}): Promise<Record<string, CandidateNote[]>> => {
  const response = await axios.post(
    `${process.env.REACT_APP_KORU_API}/analyzed-contract-notes?type=getAllForDocument`,
    { documentId },
    auth
  );

  return buildCandidateNotesByTargetId({
    noteDocuments: extractAnalyzedContractNoteDocuments(response.data),
    fallbackOwner
  });
};

const renderWatchPoints = (value: string[] | undefined) => {
  if (!value || value.length === 0) {
    return null;
  }
  return (
    <ul className="list-disc space-y-1 pl-5">
      {value.map((watchpoint) => (
        <li key={watchpoint} className="list-item">
          {watchpoint}
        </li>
      ))}
    </ul>
  );
};

const formatComparisonResultLabel = (value: string) =>
  COMPARISON_NORMALIZED_TEXT[value]?.text ?? value;

const formatComparisonResultColor = (value: string) =>
  COMPARISON_NORMALIZED_TEXT[value]?.color || undefined;

const formatAnalysisFilterLabel = (value: AnalysisFilter) => {
  if (value === NOTES_FILTER) {
    return 'Notes';
  }

  if (value === INCLUDED_SUMMARY_FILTER) {
    return 'Included in Summary';
  }

  return formatComparisonResultLabel(value);
};

const getOutOfScopeSentenceIds = (item: OutOfScope): string[] => {
  return getOutOfScopeItemSentenceIds(item);
};

const getOutOfScopeSourceText = (item: OutOfScope) =>
  item.source_text || item.sourceText || '';

const buildCompanionNoteEntries = (notes: CandidateNote[]) =>
  notes.map((note) => ({
    text: note.text,
    createdBy: note.owner || 'Unknown user',
    createdOn: new Date(note.date)
  }));

const buildCandidateCompanionText = ({
  evaluation
}: {
  evaluation: Evaluation5E;
}) => renderValue(evaluation.evidence?.evaluated_source_text);

const buildOutOfScopeCompanionText = ({ item }: { item: OutOfScope }) =>
  renderValue(getOutOfScopeSourceText(item));

const getCompanionDocumentBaseName = (
  fileName: string | undefined,
  fallback: string
) => {
  const normalizedFileName = toStringValue(fileName).trim();
  const baseName = normalizedFileName.replace(/\.[^/.]+$/, '').trim();

  return baseName || fallback;
};

const sanitizeCompanionDocumentFileName = (value: string) =>
  value
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getDimensionGroupValues = (evaluation: Evaluation5E) => {
  const benchmarkDimension =
    evaluation.dimension_assessment?.benchmark_dimension?.trim() ||
    'Unspecified benchmark dimension';
  const evaluatedDimension =
    evaluation.dimension_assessment?.evaluated_dimension?.trim() ||
    'Unspecified evaluated dimension';

  return {
    benchmarkDimension,
    evaluatedDimension,
    groupKey: `${benchmarkDimension}|||${evaluatedDimension}`
  };
};

const getComparisonResultCardClasses = (
  result: ComparisonResultFilter,
  isActive: boolean
) => {
  const activeState = isActive
    ? 'ring-2 ring-offset-2 shadow-lg scale-[1.01]'
    : 'opacity-90 hover:opacity-100 hover:-translate-y-0.5';

  switch (result) {
    case 'stronger_than_benchmark':
      return `border-red-300 bg-red-50 text-red-900 ${activeState}`;
    case 'aligned_with_benchmark':
      return `border-sky-300 bg-sky-50 text-sky-900 ${activeState}`;
    case 'weaker_than_benchmark':
      return `border-emerald-300 bg-emerald-50 text-emerald-900 ${activeState}`;
    case 'missing_benchmarked_element':
      return `border-amber-300 bg-amber-50 text-amber-900 ${activeState}`;
    case 'unclear_or_not_safely_comparable':
      return `border-violet-300 bg-violet-50 text-violet-900 ${activeState}`;
    case 'not_applicable':
      return `border-slate-300 bg-slate-100 text-slate-900 ${activeState}`;
    default:
      return `border-slate-300 bg-white text-slate-900 ${activeState}`;
  }
};

const getAnalysisFilterCardClasses = (
  result: AnalysisFilter,
  isActive: boolean
) => {
  const activeState = isActive
    ? 'ring-2 ring-offset-2 shadow-lg scale-[1.01]'
    : 'opacity-90 hover:opacity-100 hover:-translate-y-0.5';

  if (result === NOTES_FILTER) {
    return `border-[#56dac5] bg-[#e9fbf8] text-[#006f62] ${activeState}`;
  }

  if (result === INCLUDED_SUMMARY_FILTER) {
    return `border-emerald-300 bg-emerald-50 text-emerald-900 ${activeState}`;
  }

  return getComparisonResultCardClasses(result, isActive);
};

const handleInteractiveCardKeyDown = (
  event: KeyboardEvent<HTMLDivElement>,
  onActivate: () => void
) => {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }

  event.preventDefault();
  onActivate();
};

type NoteActionsMenuPosition = {
  top: number;
  left: number;
};

const FloatingNoteActionsMenu = ({
  anchorRect,
  isDeleting,
  onEdit,
  onDelete,
  onClose
}: {
  anchorRect: DOMRect | null;
  isDeleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
}) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<NoteActionsMenuPosition | null>(
    null
  );

  useLayoutEffect(() => {
    if (!anchorRect) {
      return;
    }

    const updatePosition = () => {
      const menuWidth = menuRef.current?.offsetWidth || 176;
      const menuHeight = menuRef.current?.offsetHeight || 96;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 8;

      let left = anchorRect.right - menuWidth;
      let top = anchorRect.bottom + margin;

      if (left < margin) {
        left = margin;
      }

      if (left + menuWidth > viewportWidth - margin) {
        left = viewportWidth - menuWidth - margin;
      }

      if (top + menuHeight > viewportHeight - margin) {
        top = anchorRect.top - menuHeight - margin;
      }

      if (top < margin) {
        top = margin;
      }

      setPosition({ top, left });
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRect]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (menuRef.current?.contains(target)) {
        return;
      }

      onClose();
    };

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  if (!anchorRect || !position) {
    return null;
  }

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[9999] w-44 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-2xl ring-1 ring-black/5"
      style={{ top: position.top, left: position.left }}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-slate-700 transition hover:bg-slate-50"
        onClick={() => {
          onEdit();
          onClose();
        }}
      >
        <Pencil size={14} />
        <span>Edit note</span>
      </button>
      <button
        type="button"
        disabled={isDeleting}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:text-slate-400"
        onClick={() => {
          onDelete();
          onClose();
        }}
      >
        <Trash2 size={14} />
        <span>{isDeleting ? 'Deleting...' : 'Delete note'}</span>
      </button>
    </div>,
    document.body
  );
};

const CandidateNotesPanel = ({
  notes,
  noteValue,
  isPosting,
  showComposer,
  currentUserId,
  openActionsNoteId,
  editingNoteId,
  editNoteValue,
  updatingNoteId,
  deletingNoteId,
  openActionsAnchorRect,
  onCloseActions,
  notePanelRef,
  onNoteChange,
  onPost,
  onToggleActions,
  onStartEdit,
  onEditNoteChange,
  onCancelEdit,
  onSaveEdit,
  onDelete
}: {
  notes: CandidateNote[];
  noteValue: string;
  isPosting: boolean;
  showComposer: boolean;
  currentUserId: string;
  openActionsNoteId: string | null;
  editingNoteId: string | null;
  editNoteValue: string;
  updatingNoteId: string | null;
  deletingNoteId: string | null;
  openActionsAnchorRect: DOMRect | null;
  onCloseActions: () => void;
  notePanelRef?: (node: HTMLDivElement | null) => void;
  onNoteChange: (value: string) => void;
  onPost: () => void;
  onToggleActions: (noteId: string, anchorRect: DOMRect) => void;
  onStartEdit: (note: CandidateNote) => void;
  onEditNoteChange: (value: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: (note: CandidateNote) => void;
  onDelete: (note: CandidateNote) => void;
}) => {
  const hasNoteText = noteValue.trim().length > 0;
  const lastNoteId = notes[notes.length - 1]?.id;

  return (
    <div
      ref={notePanelRef}
      className="mx-6 mb-6 rounded-xl border border-slate-200 bg-white/90 p-4 text-slate-900 shadow-sm ring-1 ring-black/5"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          Notes
        </div>
        <div className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
          {notes.length}
        </div>
      </div>

      {notes.length > 0 ? (
        <div className="mt-3 max-h-40 space-y-2 overflow-y-auto pr-1">
          {notes.map((note) => {
            const isLastNote = note.id === lastNoteId;
            const canManageNote =
              isLastNote &&
              !!currentUserId &&
              note.userId === currentUserId &&
              !!note.noteDocumentId &&
              !!note.textId;
            const isEditing = editingNoteId === note.id;
            const isUpdating = updatingNoteId === note.id;
            const isDeleting = deletingNoteId === note.id;
            const isActionsOpen = openActionsNoteId === note.id;

            return (
              <div
                key={note.id}
                className="relative rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
              >
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editNoteValue}
                      rows={3}
                      className="min-h-[84px] w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm leading-5 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                      onChange={(event) => onEditNoteChange(event.target.value)}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:bg-white"
                        onClick={onCancelEdit}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={!editNoteValue.trim() || isUpdating}
                        className="rounded-full border border-[#56dac5] bg-[#56dac5] px-3 py-1 text-xs font-semibold text-white transition hover:border-[#00a28f] hover:bg-[#00a28f] disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-200 disabled:text-slate-500"
                        onClick={() => onSaveEdit(note)}
                      >
                        {isUpdating ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="pr-10 text-sm leading-5 text-slate-800">
                    {note.text}
                  </div>
                )}

                <div className="mt-2 flex items-center justify-between gap-3 border-t border-slate-200 pt-2 text-xs text-slate-500">
                  <span>{note.owner}</span>
                  <div className="flex items-center gap-2">
                    <span>{new Date(note.date).toLocaleDateString()}</span>
                    {canManageNote && !isEditing ? (
                      <>
                        <button
                          type="button"
                          aria-label="More actions"
                          className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#56dac5]/40"
                          onClick={(event) => {
                            event.stopPropagation();
                            onToggleActions(
                              note.id,
                              event.currentTarget.getBoundingClientRect()
                            );
                          }}
                        >
                          <MoreVertical size={16} />
                        </button>
                        {isActionsOpen ? (
                          <FloatingNoteActionsMenu
                            anchorRect={openActionsAnchorRect}
                            isDeleting={isDeleting}
                            onEdit={() => onStartEdit(note)}
                            onDelete={() => onDelete(note)}
                            onClose={onCloseActions}
                          />
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {showComposer ? (
        <div className="mt-3 flex items-start gap-2">
          <textarea
            value={noteValue}
            rows={3}
            className="min-h-[84px] flex-1 resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm leading-5 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            placeholder="Add a note..."
            onChange={(event) => onNoteChange(event.target.value)}
          />
          <button
            type="button"
            aria-label="Post note"
            disabled={!hasNoteText || isPosting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#56dac5] bg-[#56dac5] text-white shadow-sm transition hover:border-[#00a28f] hover:bg-[#00a28f] focus:outline-none focus:ring-2 focus:ring-[#56dac5]/40 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-200 disabled:text-slate-500"
            onClick={onPost}
          >
            {isPosting ? <Spinner /> : <SendHorizontal size={18} />}
          </button>
        </div>
      ) : null}
    </div>
  );
};

interface AnalyzedContractViewerProps {
  onSelectSentenceIds?: (sentenceIds: string[]) => void;
  onLoadingStateChange?: (isLoading: boolean) => void;
}

const AnalyzedContractViewer: React.FC<AnalyzedContractViewerProps> = ({
  onSelectSentenceIds,
  onLoadingStateChange
}) => {
  const { auth, token, user, userM, isDeveloper } = useContext(UserContext);
  const { currentDoc, sentences } = useContext(DocumentContext);
  const { setAlert } = useAlert();
  const containerRef = useRef<HTMLDivElement>(null);
  const notePanelRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [evaluations, setEvaluations] = useState<CandidateEvaluation[]>([]);
  const [notesByTargetId, setNotesByTargetId] = useState<
    Record<string, CandidateNote[]>
  >({});
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [openNoteCardKey, setOpenNoteCardKey] = useState<string | null>(null);
  const [postingNoteKey, setPostingNoteKey] = useState<string | null>(null);
  const [openActionsNoteId, setOpenActionsNoteId] = useState<string | null>(
    null
  );
  const [openActionsAnchorRect, setOpenActionsAnchorRect] =
    useState<DOMRect | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteValue, setEditNoteValue] = useState<string>('');
  const [updatingNoteId, setUpdatingNoteId] = useState<string | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [analyzedContractJson, setAnalyzedContractJson] = useState<unknown>(
    null
  );
  const [originalAnalyzedContractJson, setOriginalAnalyzedContractJson] =
    useState<unknown>(null);
  const [activeFilter, setActiveFilter] = useState<AnalysisFilter | null>(null);
  const [activeDimensionGroupKey, setActiveDimensionGroupKey] = useState<
    string | null
  >(null);
  const [activeCardKey, setActiveCardKey] = useState<string | null>(null);
  const [summaryInclusionByCardKey, setSummaryInclusionByCardKey] = useState<
    Record<string, boolean>
  >({});
  const [
    persistedSummaryInclusionByCardKey,
    setPersistedSummaryInclusionByCardKey
  ] = useState<Record<string, boolean>>({});
  const [savingIncludedItems, setSavingIncludedItems] =
    useState<boolean>(false);
  const [downloadingCompanionDocument, setDownloadingCompanionDocument] =
    useState<boolean>(false);
  const [outOfScopeItems, setOutOfScopeItems] = useState<OutOfScope[]>([]);
  const documentId = searchParams.get('documentId');
  const showingOutOfScopeItems = activeFilter === 'not_applicable';
  const showingNotesItems = activeFilter === NOTES_FILTER;
  const showingIncludedSummaryItems =
    activeFilter === INCLUDED_SUMMARY_FILTER;
  const hasAnalysisContent =
    evaluations.length > 0 || outOfScopeItems.length > 0;

  const sentenceOrderById = useMemo(
    () =>
      sentences.reduce(
        (accumulator, sentence) => {
          if (!Number.isFinite(sentence.PageNumber)) {
            return accumulator;
          }

          const minYFromOutline = Array.isArray(sentence.outline)
            ? sentence.outline.reduce(
                (currentMin, outline) => Math.min(currentMin, outline.minY),
                Number.POSITIVE_INFINITY
              )
            : Number.POSITIVE_INFINITY;
          const sentenceOrder: SentenceDocumentOrder = {
            minY: Number.isFinite(minYFromOutline)
              ? minYFromOutline
              : Number.POSITIVE_INFINITY,
            pageNumber: sentence.PageNumber,
            sentenceIndex: Number.isFinite(sentence.sentenceIndex)
              ? sentence.sentenceIndex
              : Number.MAX_SAFE_INTEGER
          };

          const sentenceId = normalizeNoteTargetId(sentence.sentenceId);
          const databaseId = normalizeNoteTargetId(sentence._id);

          if (sentenceId) {
            accumulator[sentenceId] = sentenceOrder;
          }

          if (databaseId) {
            accumulator[databaseId] = sentenceOrder;
          }

          return accumulator;
        },
        {} as Record<string, SentenceDocumentOrder>
      ),
    [sentences]
  );

  const noteCount = useMemo(
    () =>
      Object.values(notesByTargetId).reduce(
        (count, notes) => count + notes.length,
        0
      ),
    [notesByTargetId]
  );
  const includedSummaryCount = useMemo(
    () => Object.values(summaryInclusionByCardKey).filter(Boolean).length,
    [summaryInclusionByCardKey]
  );

  const comparisonResultCounts = useMemo(
    () =>
      RESULT_FILTER_OPTIONS.reduce(
        (counts, result) => {
          if (result === NOTES_FILTER) {
            counts[result] = noteCount;
            return counts;
          }

          if (result === INCLUDED_SUMMARY_FILTER) {
            counts[result] = includedSummaryCount;
            return counts;
          }

          counts[result] =
            result === 'not_applicable'
              ? outOfScopeItems.length
              : evaluations.filter(
                  ({ evaluation }) => evaluation.comparison_result === result
                ).length;
          return counts;
        },
        {} as Record<AnalysisFilter, number>
      ),
    [evaluations, includedSummaryCount, noteCount, outOfScopeItems.length]
  );
  const hasUnsavedIncludedItemsChanges = useMemo(
    () =>
      !areBooleanRecordsEqual(
        summaryInclusionByCardKey,
        persistedSummaryInclusionByCardKey
      ),
    [persistedSummaryInclusionByCardKey, summaryInclusionByCardKey]
  );

  const filteredEvaluations = useMemo(() => {
    if (!activeFilter) {
      return evaluations;
    }

    if (activeFilter === NOTES_FILTER) {
      return evaluations.filter(
        ({ candidateId }) =>
          getNotesForTarget({
            notesByTargetId,
            targetId: candidateId
          }).length > 0
      );
    }

    if (activeFilter === INCLUDED_SUMMARY_FILTER) {
      return evaluations.filter((candidateEvaluation, index) => {
        const cardKey =
          candidateEvaluation.cardKey ||
          `${candidateEvaluation.candidateId}-${index}`;

        return !!summaryInclusionByCardKey[cardKey];
      });
    }

    return evaluations.filter(
      ({ evaluation }) => evaluation.comparison_result === activeFilter
    );
  }, [activeFilter, evaluations, notesByTargetId, summaryInclusionByCardKey]);

  useEffect(() => {
    onLoadingStateChange?.(loading);
  }, [loading, onLoadingStateChange]);

  const groupedEvaluations = useMemo(() => {
    const groups = filteredEvaluations.reduce(
      (accumulator, candidateEvaluation, index) => {
        const { benchmarkDimension, evaluatedDimension, groupKey } =
          getDimensionGroupValues(candidateEvaluation.evaluation);

        if (!accumulator[groupKey]) {
          accumulator[groupKey] = {
            benchmarkDimension,
            evaluatedDimension,
            items: []
          };
        }

        accumulator[groupKey].items.push({
          ...candidateEvaluation,
          index
        });

        return accumulator;
      },
      {} as Record<
        string,
        {
          benchmarkDimension: string;
          evaluatedDimension: string;
          items: Array<CandidateEvaluation & { index: number }>;
        }
      >
    );

    return Object.values(groups);
  }, [filteredEvaluations]);

  const visibleGroupedEvaluations = useMemo(() => {
    if (!activeDimensionGroupKey) {
      return groupedEvaluations;
    }

    return groupedEvaluations.filter(
      ({ benchmarkDimension, evaluatedDimension }) =>
        `${benchmarkDimension}|||${evaluatedDimension}` ===
        activeDimensionGroupKey
    );
  }, [activeDimensionGroupKey, groupedEvaluations]);

  const visibleEvaluationsCount = useMemo(
    () =>
      visibleGroupedEvaluations.reduce(
        (count, group) => count + group.items.length,
        0
      ),
    [visibleGroupedEvaluations]
  );
  const outOfScopeItemsWithNotes = useMemo(
    () =>
      outOfScopeItems.flatMap((item, index) => {
        const outOfScopeKey = getOutOfScopeCardKey(item, index);
        const outOfScopeId = getOutOfScopeTargetId(item, outOfScopeKey);
        const notes = getNotesForTarget({
          notesByTargetId,
          targetId: outOfScopeId,
          fallbackTargetIds: [
            outOfScopeKey,
            item.out_of_scope_id,
            item.signal_id,
            item.context_id
          ]
        });

        return notes.length > 0 ? [{ item, index }] : [];
      }),
    [notesByTargetId, outOfScopeItems]
  );
  const outOfScopeItemsIncludedInSummary = useMemo(
    () =>
      outOfScopeItems.flatMap((item, index) => {
        const outOfScopeKey = getOutOfScopeCardKey(item, index);

        return summaryInclusionByCardKey[outOfScopeKey]
          ? [{ item, index }]
          : [];
      }),
    [outOfScopeItems, summaryInclusionByCardKey]
  );
  const visibleItemCount = showingOutOfScopeItems
    ? outOfScopeItems.length
    : showingNotesItems
      ? visibleEvaluationsCount + outOfScopeItemsWithNotes.length
      : showingIncludedSummaryItems
        ? visibleEvaluationsCount + outOfScopeItemsIncludedInSummary.length
      : visibleEvaluationsCount;

  const getNoteOwner = () =>
    user?.name?.trim() || user?.email?.trim() || 'Unknown user';
  const currentUserId =
    normalizeNoteTargetId(
      (userM as unknown as { _id?: string })?._id || userM?.sub || user?.sub
    ) || '';
  useUnsavedChangesWarning(
    hasUnsavedIncludedItemsChanges,
    'You have unsaved companion document inclusion changes. Are you sure you want to leave this page?'
  );
  const companionDocumentBaseName = useMemo(
    () =>
      getCompanionDocumentBaseName(
        currentDoc?._id === documentId ? currentDoc.documentFileName : '',
        documentId ? `analyzed-contract-${documentId}` : 'analyzed-contract'
      ),
    [currentDoc?._id, currentDoc?.documentFileName, documentId]
  );

  const companionDocumentSections = useMemo(() => {
    const fallbackOrder = {
      pageNumber: 'Unspecified' as const,
      sortMinY: Number.POSITIVE_INFINITY,
      sortPage: Number.MAX_SAFE_INTEGER,
      sortSentenceIndex: Number.MAX_SAFE_INTEGER
    };

    const getDocumentOrderForSentenceIds = (sentenceIds: string[]) => {
      const sentenceOrders = sentenceIds
        .map((sentenceId) => sentenceOrderById[normalizeNoteTargetId(sentenceId)])
        .filter(
          (sentenceOrder): sentenceOrder is SentenceDocumentOrder =>
            !!sentenceOrder
        )
        .sort((leftOrder, rightOrder) => {
          if (leftOrder.pageNumber !== rightOrder.pageNumber) {
            return leftOrder.pageNumber - rightOrder.pageNumber;
          }

          if (leftOrder.minY !== rightOrder.minY) {
            return leftOrder.minY - rightOrder.minY;
          }

          return leftOrder.sentenceIndex - rightOrder.sentenceIndex;
        });

      const topMostSentenceOrder = sentenceOrders[0];

      if (!topMostSentenceOrder) {
        return fallbackOrder;
      }

      return {
        pageNumber: topMostSentenceOrder.pageNumber,
        sortMinY: topMostSentenceOrder.minY,
        sortPage: topMostSentenceOrder.pageNumber,
        sortSentenceIndex: topMostSentenceOrder.sentenceIndex
      };
    };

    const candidateEntries = evaluations.flatMap(
      ({ candidateId, evaluation, cardKey }, index) => {
        const resolvedCardKey = cardKey || `${candidateId}-${index}`;

        if (!summaryInclusionByCardKey[resolvedCardKey]) {
          return [];
        }

        const sentenceIds = evaluation.evidence?.evaluated_sentence_ids || [];
        const notes = getNotesForTarget({
          notesByTargetId,
          targetId: candidateId
        });
        const documentOrder = getDocumentOrderForSentenceIds(sentenceIds);

        return [
          {
            comparisonColor: evaluation.comparison_result
              ? formatComparisonResultColor(evaluation.comparison_result)
              : undefined,
            comparisonLabel: evaluation.comparison_result
              ? formatComparisonResultLabel(evaluation.comparison_result)
              : undefined,
            ...documentOrder,
            originalIndex: index,
            text: buildCandidateCompanionText({
              evaluation
            }),
            notes: buildCompanionNoteEntries(notes)
          }
        ] satisfies CompanionDocumentEntry[];
      }
    );

    const outOfScopeEntries = outOfScopeItems.flatMap((item, index) => {
      const outOfScopeKey = getOutOfScopeCardKey(item, index);

      if (!summaryInclusionByCardKey[outOfScopeKey]) {
        return [];
      }

      const sentenceIds = getOutOfScopeSentenceIds(item);
      const outOfScopeId = getOutOfScopeTargetId(item, outOfScopeKey);
      const notes = getNotesForTarget({
        notesByTargetId,
        targetId: outOfScopeId,
        fallbackTargetIds: [
          outOfScopeKey,
          item.out_of_scope_id,
          item.signal_id,
          item.context_id
        ]
      });
      const documentOrder = getDocumentOrderForSentenceIds(sentenceIds);

      return [
        {
          comparisonColor: formatComparisonResultColor('not_applicable'),
          comparisonLabel: formatComparisonResultLabel('not_applicable'),
          ...documentOrder,
          originalIndex: evaluations.length + index,
          text: buildOutOfScopeCompanionText({
            item
          }),
          notes: buildCompanionNoteEntries(notes)
        }
      ] satisfies CompanionDocumentEntry[];
    });

    const sortedEntries = [...candidateEntries, ...outOfScopeEntries].sort(
      (leftEntry, rightEntry) => {
        if (leftEntry.sortPage !== rightEntry.sortPage) {
          return leftEntry.sortPage - rightEntry.sortPage;
        }

        if (leftEntry.sortMinY !== rightEntry.sortMinY) {
          return leftEntry.sortMinY - rightEntry.sortMinY;
        }

        if (leftEntry.sortSentenceIndex !== rightEntry.sortSentenceIndex) {
          return leftEntry.sortSentenceIndex - rightEntry.sortSentenceIndex;
        }

        return leftEntry.originalIndex - rightEntry.originalIndex;
      }
    );

    return sortedEntries.reduce((sections, entry) => {
      const lastSection = sections[sections.length - 1];

      if (lastSection && lastSection.pageNumber === entry.pageNumber) {
        lastSection.items.push({
          comparisonColor: entry.comparisonColor,
          comparisonLabel: entry.comparisonLabel,
          text: entry.text,
          notes: entry.notes
        });

        return sections;
      }

      sections.push({
        pageNumber: entry.pageNumber,
        items: [
          {
            comparisonColor: entry.comparisonColor,
            comparisonLabel: entry.comparisonLabel,
            text: entry.text,
            notes: entry.notes
          }
        ]
      });

      return sections;
    }, [] as ParagraphSection[]);
  }, [
    evaluations,
    notesByTargetId,
    outOfScopeItems,
    sentenceOrderById,
    summaryInclusionByCardKey
  ]);

  const setNoteDraft = (cardKey: string, value: string) => {
    setNoteDrafts((previousDrafts) => ({
      ...previousDrafts,
      [cardKey]: value
    }));
  };

  const setIncludeInSummary = (cardKey: string, value: boolean) => {
    setSummaryInclusionByCardKey((previousState) => ({
      ...previousState,
      [cardKey]: value
    }));
  };

  const saveIncludedItems = async () => {
    if (!documentId) {
      setAlert('Missing document id. Unable to save included items.', 'error', 4);
      return;
    }

    if (!analyzedContractJson) {
      setAlert(
        'The analyzed contract JSON is not loaded. Unable to save included items.',
        'error',
        4
      );
      return;
    }

    setSavingIncludedItems(true);

    try {
      const nextAnalyzedContractJson = cloneJsonValue(analyzedContractJson);
      let updatedRecordCount = 0;

      evaluations.forEach((candidateEvaluation, index) => {
        const cardKey =
          candidateEvaluation.cardKey ||
          `${candidateEvaluation.candidateId}-${index}`;

        if (!candidateEvaluation.sourcePath?.length) {
          return;
        }

        const didUpdate = updateRecordAtPath(
          nextAnalyzedContractJson,
          candidateEvaluation.sourcePath,
          (record) => {
            record[INCLUDE_IN_COMPANION_DOCUMENT_FIELD] =
              !!summaryInclusionByCardKey[cardKey];
          }
        );

        if (didUpdate) {
          updatedRecordCount += 1;
        }
      });

      outOfScopeItems.forEach((item, index) => {
        const cardKey = getOutOfScopeCardKey(item, index);

        if (!item.sourcePath?.length) {
          return;
        }

        const didUpdate = updateRecordAtPath(
          nextAnalyzedContractJson,
          item.sourcePath,
          (record) => {
            record[INCLUDE_IN_COMPANION_DOCUMENT_FIELD] =
              !!summaryInclusionByCardKey[cardKey];
          }
        );

        if (didUpdate) {
          updatedRecordCount += 1;
        }
      });

      if (updatedRecordCount === 0) {
        throw new Error('No analyzed contract entries were available to save.');
      }

      const presignedUrl = await axios.post(
        `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=${SAVE_ANALYZED_CONTRACT_TYPE}`,
        { documentId },
        auth
      );
      const uploadUrl = presignedUrl?.data?.url;

      if (!uploadUrl || uploadUrl === 'locked') {
        throw new Error('No upload URL was returned for the analyzed contract.');
      }

      const blob = new Blob([JSON.stringify(nextAnalyzedContractJson)], {
        type: 'application/json'
      });
      await axios.put(uploadUrl, blob, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      setAnalyzedContractJson(nextAnalyzedContractJson);
      setPersistedSummaryInclusionByCardKey({
        ...summaryInclusionByCardKey
      });
      setAlert('Companion document selections saved.', 'success', 3);
    } catch (saveError) {
      setAlert(
        getErrorMessage(
          saveError,
          'Unable to save companion document selections.'
        ),
        'error',
        4
      );
    } finally {
      setSavingIncludedItems(false);
    }
  };

  const toggleNotePanel = (cardKey: string) => {
    setOpenNoteCardKey((currentValue) =>
      currentValue === cardKey ? null : cardKey
    );
  };

  const postCandidateNote = async ({
    cardKey,
    target
  }: {
    cardKey: string;
    target: AnalyzedContractNoteTarget;
  }) => {
    const noteText = noteDrafts[cardKey]?.trim();

    if (!noteText) {
      return;
    }

    if (!documentId) {
      setAlert('Missing document id. Unable to post note.', 'error', 4);
      return;
    }

    setPostingNoteKey(cardKey);

    try {
      const createdNote = await createCandidateNote(
        {
          documentId,
          noteText,
          owner: getNoteOwner(),
          target
        },
        auth
      );
      const noteForState = {
        ...createdNote,
        userId: createdNote.userId || currentUserId
      };
      const normalizedTarget = normalizeNoteTarget(target);
      const noteTargetId =
        normalizedTarget.type === 'candidate'
          ? getNoteTargetCandidateId(normalizedTarget)
          : getNoteTargetOutOfScopeId(normalizedTarget);

      setNotesByTargetId((previousNotes) => ({
        ...previousNotes,
        [noteTargetId]: [...(previousNotes[noteTargetId] || []), noteForState]
      }));
      setNoteDraft(cardKey, '');
      setAlert('Note posted.', 'success', 3);
    } catch (noteError) {
      setAlert(getErrorMessage(noteError, 'Unable to post note.'), 'error', 4);
    } finally {
      setPostingNoteKey(null);
    }
  };

  const canMutateNote = (note: CandidateNote) =>
    !!currentUserId &&
    note.userId === currentUserId &&
    !!note.noteDocumentId &&
    !!note.textId &&
    Object.values(notesByTargetId).some(
      (notes) => notes[notes.length - 1]?.id === note.id
    );

  const startEditingNote = (note: CandidateNote) => {
    if (!canMutateNote(note)) {
      setAlert(
        'Only the latest note can be edited by its author.',
        'warning',
        4
      );
      return;
    }

    setEditingNoteId(note.id);
    setEditNoteValue(note.text);
    setOpenActionsNoteId(null);
    setOpenActionsAnchorRect(null);
  };

  const cancelEditingNote = () => {
    setEditingNoteId(null);
    setEditNoteValue('');
  };

  const saveEditedNote = async (note: CandidateNote) => {
    const updatedText = editNoteValue.trim();

    if (!updatedText) {
      return;
    }

    if (!documentId) {
      setAlert('Missing document id. Unable to update note.', 'error', 4);
      return;
    }

    if (!canMutateNote(note)) {
      setAlert(
        'Only the latest note can be edited by its author.',
        'warning',
        4
      );
      return;
    }

    setUpdatingNoteId(note.id);

    try {
      await updateCandidateNoteText(
        {
          documentId,
          noteText_id: note.textId,
          noteId: note.noteDocumentId,
          text: updatedText
        },
        auth
      );

      setNotesByTargetId((previousNotes) =>
        Object.entries(previousNotes).reduce(
          (nextNotes, [targetId, notes]) => {
            nextNotes[targetId] = notes.map((currentNote) =>
              currentNote.id === note.id
                ? { ...currentNote, text: updatedText }
                : currentNote
            );
            return nextNotes;
          },
          {} as Record<string, CandidateNote[]>
        )
      );
      cancelEditingNote();
      setAlert('Note updated.', 'success', 3);
    } catch (noteError) {
      setAlert(
        getErrorMessage(noteError, 'Unable to update note.'),
        'error',
        4
      );
    } finally {
      setUpdatingNoteId(null);
    }
  };

  const deleteNote = async (note: CandidateNote) => {
    if (!documentId) {
      setAlert('Missing document id. Unable to delete note.', 'error', 4);
      return;
    }

    if (!canMutateNote(note)) {
      setAlert(
        'Only the latest note can be deleted by its author.',
        'warning',
        4
      );
      return;
    }

    setDeletingNoteId(note.id);

    try {
      await deleteCandidateNoteText(
        {
          documentId,
          _id: note.noteDocumentId,
          textId: note.textId
        },
        auth
      );

      setNotesByTargetId((previousNotes) =>
        Object.entries(previousNotes).reduce(
          (nextNotes, [targetId, notes]) => {
            const remainingNotes = notes.filter(
              (currentNote) => currentNote.id !== note.id
            );

            if (remainingNotes.length > 0) {
              nextNotes[targetId] = remainingNotes;
            }

            return nextNotes;
          },
          {} as Record<string, CandidateNote[]>
        )
      );
      setOpenActionsNoteId(null);
      setOpenActionsAnchorRect(null);
      setAlert('Note deleted.', 'success', 3);
    } catch (noteError) {
      setAlert(
        getErrorMessage(noteError, 'Unable to delete note.'),
        'error',
        4
      );
    } finally {
      setDeletingNoteId(null);
    }
  };

  const renderNoteToggleButton = (cardKey: string, label: string) => {
    const isOpen = openNoteCardKey === cardKey;

    return (
      <button
        type="button"
        aria-label={label}
        aria-pressed={isOpen}
        className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-white/90 text-slate-700 shadow-sm transition hover:bg-white hover:text-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-400 ${
          isOpen ? 'border-sky-400 ring-2 ring-sky-100' : 'border-slate-200'
        }`}
        onClick={(event) => {
          event.stopPropagation();
          toggleNotePanel(cardKey);
        }}
      >
        <MessageSquarePlus size={18} />
      </button>
    );
  };

  const renderIncludeInSummaryCheckbox = (cardKey: string) => (
    <label
      className="flex cursor-pointer items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-white"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={!!summaryInclusionByCardKey[cardKey]}
        className="h-4 w-4 cursor-pointer accent-[#56dac5]"
        onChange={(event) =>
          setIncludeInSummary(cardKey, event.currentTarget.checked)
        }
      />
      <span>Include in summary</span>
    </label>
  );

  const downloadCompanionDocument = async () => {
    if (companionDocumentSections.length === 0) {
      setAlert(
        'Select at least one candidate or out-of-scope item to include in the summary.',
        'warning',
        4
      );
      return;
    }

    setDownloadingCompanionDocument(true);

    try {
      const blob = await buildNotesDocxBlob({
        headerText: `${companionDocumentBaseName} Companion Document`,
        footerText:
          'Confidential and Proprietary. This document contains sensitive contractual analysis intended solely for the authorized recipient. Unauthorized use, disclosure, copying, or distribution is prohibited.',
        sections: companionDocumentSections
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = `${sanitizeCompanionDocumentFileName(
        companionDocumentBaseName
      )} - Companion Document.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 0);
      setAlert('Companion document downloaded.', 'success', 3);
    } catch (downloadError) {
      setAlert(
        getErrorMessage(
          downloadError,
          'Unable to generate the companion document.'
        ),
        'error',
        4
      );
    } finally {
      setDownloadingCompanionDocument(false);
    }
  };

  const downloadAnalyzedContractResults = () => {
    if (!originalAnalyzedContractJson) {
      setAlert('The analyzed contract JSON is not loaded.', 'error', 4);
      return;
    }

    const downloadableAnalyzedContractJson = cloneJsonValue(
      originalAnalyzedContractJson
    );

    if (isRecord(downloadableAnalyzedContractJson)) {
      downloadableAnalyzedContractJson.candidates =
        removeEmbeddingFieldsFromCandidates(
          downloadableAnalyzedContractJson.candidates
        );
    }

    const blob = new Blob(
      [JSON.stringify(downloadableAnalyzedContractJson, null, 2)],
      {
        type: 'application/json'
      }
    );
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = downloadUrl;
    link.download = `${sanitizeCompanionDocumentFileName(
      companionDocumentBaseName
    )} - Results.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 0);
  };

  useEffect(() => {
    setNotesByTargetId({});
    setNoteDrafts({});
    setOpenNoteCardKey(null);
    setPostingNoteKey(null);
    setOpenActionsNoteId(null);
    setOpenActionsAnchorRect(null);
    setEditingNoteId(null);
    setEditNoteValue('');
    setUpdatingNoteId(null);
    setDeletingNoteId(null);
    setAnalyzedContractJson(null);
    setOriginalAnalyzedContractJson(null);
    setSummaryInclusionByCardKey({});
    setPersistedSummaryInclusionByCardKey({});
    setSavingIncludedItems(false);
    setDownloadingCompanionDocument(false);
  }, [documentId]);

  useEffect(() => {
    if (!openNoteCardKey) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      notePanelRefs.current[openNoteCardKey]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [openNoteCardKey]);

  useEffect(() => {
    let cancelled = false;

    const loadAnalyzedContract = async () => {
      if (!token || !documentId) {
        return;
      }

      setLoading(true);
      setError('');

      try {
        const presignedUrl = await axios.post(
          `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getAnalyzedContract`,
          { documentId },
          auth
        );
        const downloadUrl = presignedUrl?.data?.url;

        if (!downloadUrl || downloadUrl === 'locked') {
          throw new Error('No analyzed contract presigned URL was returned.');
        }

        const response = await axios.get(downloadUrl, {
          responseType: 'json'
        });

        if (cancelled) {
          return;
        }

        const loadedEvaluations = extractCandidateEvaluations(
          response.data.candidates,
          ['candidates']
        ).map((candidateEvaluation, index) => ({
          ...candidateEvaluation,
          cardKey: `${candidateEvaluation.candidateId}-${index}`
        }));
        const loadedOutOfScopeItems = extractOutOfScopeItems(
          response.data.out_of_scope,
          ['out_of_scope']
        );
        const loadedSummaryInclusionState = buildSummaryInclusionState({
          evaluations: loadedEvaluations,
          outOfScopeItems: loadedOutOfScopeItems
        });

        setAnalyzedContractJson(response.data);
        setOriginalAnalyzedContractJson(cloneJsonValue(response.data));
        setEvaluations(loadedEvaluations);
        setOutOfScopeItems(loadedOutOfScopeItems);
        setSummaryInclusionByCardKey(loadedSummaryInclusionState);
        setPersistedSummaryInclusionByCardKey(loadedSummaryInclusionState);
        setActiveFilter(null);
        setActiveDimensionGroupKey(null);
        setActiveCardKey(null);

        try {
          const loadedNotesByTargetId = await getCandidateNotesForDocument({
            documentId,
            auth,
            fallbackOwner: getNoteOwner()
          });

          if (!cancelled) {
            setNotesByTargetId(loadedNotesByTargetId);
          }
        } catch (notesError) {
          if (!cancelled) {
            setNotesByTargetId({});
            setAlert(
              getErrorMessage(notesError, 'Unable to load notes.'),
              'warning',
              4
            );
          }
        }
      } catch (loadError) {
        if (!cancelled) {
          if (axios.isAxiosError(loadError)) {
            setError(loadError.response?.data?.message || loadError.message);
          } else if (loadError instanceof Error) {
            setError(loadError.message);
          } else {
            setError('Unable to load analyzed contract.');
          }
          setEvaluations([]);
          setOutOfScopeItems([]);
          setNotesByTargetId({});
          setAnalyzedContractJson(null);
          setOriginalAnalyzedContractJson(null);
          setSummaryInclusionByCardKey({});
          setPersistedSummaryInclusionByCardKey({});
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAnalyzedContract();

    return () => {
      cancelled = true;
    };
  }, [auth, documentId, setAlert, token]);

  return (
    <div
      ref={containerRef}
      className="relative h-full overflow-y-scroll"
      style={{ scrollbarWidth: 'none' }}
    >
      <div className="mx-auto min-h-[85vh] w-full max-w-6xl px-4 py-6">
        <Container
          header={
            <Header
              variant="h1"
              // description={titleDescription}
              // counter={`(${visibleEvaluationsCount})`}
            >
              Analyzed Details
            </Header>
          }
        >
          <SpaceBetween size="l">
            {!documentId ? (
              <Alert type="error">
                Missing `documentId` in the search params.
              </Alert>
            ) : null}

            {error ? <Alert type="error">{error}</Alert> : null}

            {loading ? (
              <Box textAlign="center" padding={{ vertical: 'xl' }}>
                <Spinner size="large" />
              </Box>
            ) : null}

            {!loading && !error && !hasAnalysisContent ? (
              <Box margin={{ vertical: 'xs' }} textAlign="center">
                No analyzed contract items found.
              </Box>
            ) : null}

            {!loading && hasAnalysisContent ? (
              <SpaceBetween size="l">
                <Container
                  header={
                    <Header
                      variant="h2"
                      description={
                        activeFilter
                          ? `Filtering to ${formatAnalysisFilterLabel(
                              activeFilter
                            )}`
                          : 'Click a result card to filter the contract analysis.'
                      }
                    >
                      Comparison Results
                    </Header>
                  }
                >
                  <SpaceBetween size="m">
                    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 md:flex-row md:items-center md:justify-between">
                      <div className="space-y-1">
                        <div className="text-sm text-slate-700">
                          {includedSummaryCount} item
                          {includedSummaryCount === 1 ? '' : 's'} included in
                          the summary.
                        </div>
                        {hasUnsavedIncludedItemsChanges ? (
                          <div className="text-xs font-medium text-amber-700">
                            Companion document selections have unsaved changes.
                          </div>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          disabled={
                            !hasUnsavedIncludedItemsChanges || savingIncludedItems
                          }
                          className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                          onClick={saveIncludedItems}
                        >
                          {savingIncludedItems
                            ? 'Saving...'
                            : 'Save Included Items'}
                        </button>
                        <button
                          type="button"
                          disabled={
                            includedSummaryCount === 0 ||
                            downloadingCompanionDocument
                          }
                          className="inline-flex items-center justify-center rounded-full border border-[#56dac5] bg-[#56dac5] px-4 py-2 text-sm font-semibold text-white transition hover:border-[#00a28f] hover:bg-[#00a28f] disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-200 disabled:text-slate-500"
                          onClick={downloadCompanionDocument}
                        >
                          {downloadingCompanionDocument
                            ? 'Preparing document...'
                            : 'Download Companion Document'}
                        </button>
                        {isDeveloper ? (
                          <button
                            type="button"
                            disabled={!originalAnalyzedContractJson}
                            className="inline-flex items-center justify-center rounded-full border border-sky-300 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-900 transition hover:border-sky-400 hover:bg-sky-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                            onClick={downloadAnalyzedContractResults}
                          >
                            Download Results
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {RESULT_FILTER_OPTIONS.map((result) => {
                        const isActive = activeFilter === result;

                        return (
                          <button
                            key={result}
                            type="button"
                            className={`rounded-2xl border p-4 text-left transition duration-150 ${getAnalysisFilterCardClasses(
                              result,
                              isActive
                            )}`}
                            onClick={() => {
                              setActiveFilter((currentValue) =>
                                currentValue === result ? null : result
                              );
                              if (
                                result === 'not_applicable' ||
                                result === NOTES_FILTER ||
                                result === INCLUDED_SUMMARY_FILTER
                              ) {
                                setActiveDimensionGroupKey(null);
                                setActiveCardKey(null);
                                onSelectSentenceIds?.([]);
                              }
                            }}
                          >
                            {/* <div className="text-xs font-semibold uppercase tracking-[0.18em]">
                            Result
                          </div> */}
                            <div className="mt-1 text-base font-semibold leading-tight">
                              {formatAnalysisFilterLabel(result)}
                            </div>
                            <div className="mt-5 flex items-end justify-between">
                              <div className="text-4xl font-semibold">
                                {comparisonResultCounts[result]}
                              </div>
                              <div className="text-xs font-medium uppercase tracking-[0.16em]">
                                {isActive ? 'Showing' : 'View'}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {activeFilter || activeDimensionGroupKey ? (
                      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                        <div className="text-sm text-slate-700">
                          Showing {visibleItemCount}{' '}
                          {showingNotesItems
                            ? `noted item${visibleItemCount === 1 ? '' : 's'}`
                            : showingIncludedSummaryItems
                              ? `included item${visibleItemCount === 1 ? '' : 's'}`
                            : showingOutOfScopeItems
                              ? `out-of-scope item${visibleItemCount === 1 ? '' : 's'}`
                              : `candidate${visibleItemCount === 1 ? '' : 's'}`}
                          {activeFilter ? (
                            <>
                              {' '}
                              with{' '}
                              <strong>
                                {formatAnalysisFilterLabel(activeFilter)}
                              </strong>
                            </>
                          ) : null}
                          {!showingOutOfScopeItems &&
                          !showingNotesItems &&
                          !showingIncludedSummaryItems &&
                          activeDimensionGroupKey &&
                          visibleGroupedEvaluations[0] ? (
                            <>
                              {' '}
                              in{' '}
                              <strong>
                                {
                                  visibleGroupedEvaluations[0]
                                    .benchmarkDimension
                                }
                              </strong>
                              {` / ${visibleGroupedEvaluations[0].evaluatedDimension}`}
                            </>
                          ) : null}
                          .
                        </div>
                        <div className="flex items-center gap-2">
                          {!showingOutOfScopeItems &&
                          !showingNotesItems &&
                          !showingIncludedSummaryItems &&
                          activeDimensionGroupKey ? (
                            <button
                              type="button"
                              className="rounded-full border border-slate-300 px-3 py-1 text-sm font-medium text-slate-700 transition hover:bg-white"
                              onClick={() => setActiveDimensionGroupKey(null)}
                            >
                              Clear Dimension Filter
                            </button>
                          ) : null}
                          {activeFilter ? (
                            <button
                              type="button"
                              className="rounded-full border border-slate-300 px-3 py-1 text-sm font-medium text-slate-700 transition hover:bg-white"
                              onClick={() => setActiveFilter(null)}
                            >
                              Clear Filter
                            </button>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </SpaceBetween>
                </Container>

                {visibleItemCount === 0 ? (
                  <Box margin={{ vertical: 'xs' }} textAlign="center">
                    {showingNotesItems
                      ? 'No noted items found.'
                      : showingIncludedSummaryItems
                        ? 'No items are currently included in summary.'
                      : showingOutOfScopeItems
                        ? 'No out-of-scope items found.'
                        : 'No candidates match the selected filters.'}
                  </Box>
                ) : null}

                {(showingOutOfScopeItems
                  ? outOfScopeItems.map((item, index) => ({ item, index }))
                  : showingNotesItems
                    ? outOfScopeItemsWithNotes
                    : showingIncludedSummaryItems
                      ? outOfScopeItemsIncludedInSummary
                    : []
                ).map(({ item, index }) => {
                  const sentenceIds = getOutOfScopeSentenceIds(item);
                  const outOfScopeKey = getOutOfScopeCardKey(item, index);
                  const outOfScopeId = getOutOfScopeTargetId(
                    item,
                    outOfScopeKey
                  );
                  const isActive = activeCardKey === outOfScopeKey;
                  const outOfScopeNotes = getNotesForTarget({
                    notesByTargetId,
                    targetId: outOfScopeId,
                    fallbackTargetIds: [
                      outOfScopeKey,
                      item.out_of_scope_id,
                      item.signal_id,
                      item.context_id
                    ]
                  });
                  const toggleOutOfScopeHighlight = () => {
                    if (isActive) {
                      setActiveCardKey(null);
                      onSelectSentenceIds?.([]);
                      return;
                    }

                    setActiveCardKey(outOfScopeKey);
                    onSelectSentenceIds?.(sentenceIds);
                  };

                  return (
                    <div
                      key={outOfScopeKey}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isActive}
                      className={`block w-full cursor-pointer overflow-hidden rounded-2xl border text-left shadow-sm transition duration-150 focus:outline-none focus:ring-2 focus:ring-sky-400 ${getComparisonResultCardClasses(
                        'not_applicable',
                        isActive
                      )}`}
                      onClick={toggleOutOfScopeHighlight}
                      onKeyDown={(event) =>
                        handleInteractiveCardKeyDown(
                          event,
                          toggleOutOfScopeHighlight
                        )
                      }
                    >
                      <div className="flex flex-col gap-4 border-b border-black/10 px-6 py-5 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0">
                          {/* <div className="text-xs font-semibold uppercase tracking-[0.18em] text-current/70">
                                Out of Scope
                              </div> */}
                          <h2 className="mt-2 text-xl font-bold leading-tight">
                            {formatComparisonResultLabel('not_applicable')}
                          </h2>
                          <div className="mt-2 text-sm font-medium text-current/80">
                            {renderValue(item.signal_id || item.context_id)}
                          </div>
                          <div className="mt-3 text-xs font-medium uppercase tracking-[0.16em] text-current/65">
                            {sentenceIds.length
                              ? isActive
                                ? 'Highlighted'
                                : 'Click to Highlight'
                              : 'No Evidence Available'}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-3">
                          {renderIncludeInSummaryCheckbox(outOfScopeKey)}
                          {renderNoteToggleButton(
                            outOfScopeKey,
                            `Add note for ${renderValue(
                              item.signal_id || item.context_id
                            )}`
                          )}
                        </div>
                      </div>

                      <div className="space-y-5 px-6 py-5">
                        <section className="rounded-xl bg-white/70 px-4 py-4 shadow-sm ring-1 ring-black/5">
                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Clause being reviewed
                          </div>
                          <div className="mt-2 text-sm leading-6 text-slate-800">
                            {renderValue(item.source_text)}
                          </div>
                        </section>

                        <section className="rounded-xl bg-white/70 px-4 py-4 shadow-sm ring-1 ring-black/5">
                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Why this is out of scope
                          </div>
                          <div className="mt-2 text-sm leading-6 text-slate-800">
                            {renderValue(item.reason)}
                          </div>
                        </section>
                      </div>

                      {openNoteCardKey === outOfScopeKey ||
                      outOfScopeNotes.length > 0 ? (
                        <CandidateNotesPanel
                          notes={outOfScopeNotes}
                          noteValue={noteDrafts[outOfScopeKey] || ''}
                          isPosting={postingNoteKey === outOfScopeKey}
                          showComposer={openNoteCardKey === outOfScopeKey}
                          currentUserId={currentUserId}
                          openActionsNoteId={openActionsNoteId}
                          openActionsAnchorRect={openActionsAnchorRect}
                          editingNoteId={editingNoteId}
                          editNoteValue={editNoteValue}
                          updatingNoteId={updatingNoteId}
                          deletingNoteId={deletingNoteId}
                          notePanelRef={(node) => {
                            notePanelRefs.current[outOfScopeKey] = node;
                          }}
                          onNoteChange={(value) =>
                            setNoteDraft(outOfScopeKey, value)
                          }
                          onPost={() =>
                            postCandidateNote({
                              cardKey: outOfScopeKey,
                              target: {
                                type: 'out_of_scope',
                                outOfScopeId,
                                sentenceIds
                              }
                            })
                          }
                          onToggleActions={(noteId, anchorRect) => {
                            if (openActionsNoteId === noteId) {
                              setOpenActionsNoteId(null);
                              setOpenActionsAnchorRect(null);
                              return;
                            }

                            setOpenActionsNoteId(noteId);
                            setOpenActionsAnchorRect(anchorRect);
                          }}
                          onCloseActions={() => {
                            setOpenActionsNoteId(null);
                            setOpenActionsAnchorRect(null);
                          }}
                          onStartEdit={startEditingNote}
                          onEditNoteChange={setEditNoteValue}
                          onCancelEdit={cancelEditingNote}
                          onSaveEdit={saveEditedNote}
                          onDelete={deleteNote}
                        />
                      ) : null}
                    </div>
                  );
                })}

                {!showingOutOfScopeItems
                  ? visibleGroupedEvaluations.map(
                      ({ benchmarkDimension, evaluatedDimension, items }) => {
                        const groupKey = `${benchmarkDimension}|||${evaluatedDimension}`;
                        const isGroupActive =
                          activeDimensionGroupKey === groupKey;

                        return (
                          <div
                            key={`${benchmarkDimension}-${evaluatedDimension}`}
                            className="space-y-4"
                          >
                            <button
                              type="button"
                              aria-pressed={isGroupActive}
                              className={`block w-full rounded-2xl border px-5 py-4 text-left transition duration-150 ${
                                isGroupActive
                                  ? 'border-slate-300 bg-slate-100 text-slate-900 ring-2 ring-offset-2 shadow-lg scale-[1.01]'
                                  : 'border-slate-200 bg-slate-50 text-slate-900 opacity-90 hover:opacity-100 hover:-translate-y-0.5'
                              }`}
                              onClick={() =>
                                setActiveDimensionGroupKey((currentValue) =>
                                  currentValue === groupKey ? null : groupKey
                                )
                              }
                            >
                              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                Dimension Group
                              </div>
                              <div className="mt-2 text-lg font-semibold text-slate-900">
                                {benchmarkDimension}
                              </div>
                              <div className="mt-1 text-sm text-slate-600">
                                Evaluated dimension: {evaluatedDimension}
                              </div>
                            </button>

                            <div className="space-y-4">
                              {items.map(
                                ({
                                  candidateId,
                                  evaluation,
                                  index,
                                  cardKey: storedCardKey
                                }) => {
                                  const comparisonResult =
                                    isComparisonResultFilter(
                                      evaluation.comparison_result
                                    )
                                      ? evaluation.comparison_result
                                      : null;
                                  const confidence = evaluation.confidence
                                    ? evaluation.confidence
                                    : null;
                                  const cardKey =
                                    storedCardKey || `${candidateId}-${index}`;
                                  const sentenceIds =
                                    evaluation.evidence
                                      ?.evaluated_sentence_ids || [];
                                  const isActive = activeCardKey === cardKey;
                                  const notes = getNotesForTarget({
                                    notesByTargetId,
                                    targetId: candidateId
                                  });
                                  const toggleCandidateHighlight = () => {
                                    if (isActive) {
                                      setActiveCardKey(null);
                                      onSelectSentenceIds?.([]);
                                      return;
                                    }

                                    setActiveCardKey(cardKey);
                                    onSelectSentenceIds?.(sentenceIds);
                                  };

                                  return (
                                    <div
                                      key={cardKey}
                                      role="button"
                                      tabIndex={0}
                                      aria-pressed={isActive}
                                      className={`block w-full cursor-pointer overflow-hidden rounded-2xl border text-left shadow-sm transition duration-150 focus:outline-none focus:ring-2 focus:ring-sky-400 ${comparisonResult ? getComparisonResultCardClasses(comparisonResult, isActive) : isActive ? 'border-slate-300 bg-white text-slate-900 ring-2 ring-offset-2 shadow-lg scale-[1.01]' : 'border-slate-200 bg-white text-slate-900 opacity-90 hover:opacity-100 hover:-translate-y-0.5'}`}
                                      onClick={toggleCandidateHighlight}
                                      onKeyDown={(event) =>
                                        handleInteractiveCardKeyDown(
                                          event,
                                          toggleCandidateHighlight
                                        )
                                      }
                                    >
                                      <div className="flex flex-col gap-4 border-b border-black/10 px-6 py-5 md:flex-row md:items-start md:justify-between">
                                        <div className="min-w-0">
                                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-current/70">
                                            Candidate
                                          </div>
                                          {comparisonResult ? (
                                            <h2 className="mt-2 text-xl font-bold leading-tight">
                                              {`${formatComparisonResultLabel(
                                                comparisonResult
                                              )} - ${confidence} confidence `}
                                            </h2>
                                          ) : null}
                                          <div className="mt-2 text-sm font-medium text-current/80">
                                            {candidateId}
                                          </div>
                                          <div className="mt-3 text-xs font-medium uppercase tracking-[0.16em] text-current/65">
                                            {sentenceIds.length
                                              ? isActive
                                                ? 'Highlighted'
                                                : 'Click to Highlight'
                                              : 'No Evidence Available'}
                                          </div>
                                        </div>
                                        <div className="flex flex-wrap items-center justify-end gap-3">
                                          {renderIncludeInSummaryCheckbox(
                                            cardKey
                                          )}
                                          {renderNoteToggleButton(
                                            cardKey,
                                            `Add note for ${candidateId}`
                                          )}
                                        </div>
                                      </div>

                                      <div className="space-y-5 px-6 py-5">
                                        <section className="rounded-xl bg-white/70 px-4 py-4 shadow-sm ring-1 ring-black/5">
                                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                            Clause being reviewed
                                          </div>
                                          <div className="mt-2 text-sm leading-6 text-slate-800">
                                            {renderValue(
                                              evaluation.evidence
                                                ?.evaluated_source_text
                                            )}
                                          </div>
                                        </section>

                                        <section className="rounded-xl bg-white/70 px-4 py-4 shadow-sm ring-1 ring-black/5">
                                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                            Your Standard
                                          </div>
                                          <div className="mt-2 text-sm leading-6 text-slate-800">
                                            {renderValue(
                                              evaluation.basis
                                                ?.benchmark_summary
                                            )}
                                          </div>
                                        </section>

                                        <section className="rounded-xl bg-white/70 px-4 py-4 shadow-sm ring-1 ring-black/5">
                                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                            {`Why this is ${COMPARISON_NORMALIZED_TEXT[evaluation?.comparison_result!]?.text ?? evaluation?.comparison_result ?? ''}`}
                                          </div>
                                          <div className="mt-2 text-sm leading-6 text-slate-800">
                                            {renderValue(
                                              evaluation.basis?.reason
                                            )}
                                          </div>
                                        </section>
                                        {evaluation?.watchpoints &&
                                          evaluation?.watchpoints?.length >
                                            0 && (
                                            <section className="rounded-xl bg-amber-50 px-4 py-4 shadow-sm ring-1 ring-black/5">
                                              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-900">
                                                {`Things to keep an eye on`}
                                              </div>
                                              <div className="mt-2 text-sm leading-6 text-slate-800">
                                                {renderWatchPoints(
                                                  evaluation.watchpoints
                                                )}
                                              </div>
                                            </section>
                                          )}
                                      </div>

                                      {openNoteCardKey === cardKey ||
                                      notes.length > 0 ? (
                                        <CandidateNotesPanel
                                          notes={notes}
                                          noteValue={noteDrafts[cardKey] || ''}
                                          isPosting={postingNoteKey === cardKey}
                                          showComposer={
                                            openNoteCardKey === cardKey
                                          }
                                          currentUserId={currentUserId}
                                          openActionsNoteId={openActionsNoteId}
                                          openActionsAnchorRect={
                                            openActionsAnchorRect
                                          }
                                          editingNoteId={editingNoteId}
                                          editNoteValue={editNoteValue}
                                          updatingNoteId={updatingNoteId}
                                          deletingNoteId={deletingNoteId}
                                          notePanelRef={(node) => {
                                            notePanelRefs.current[cardKey] =
                                              node;
                                          }}
                                          onNoteChange={(value) =>
                                            setNoteDraft(cardKey, value)
                                          }
                                          onPost={() =>
                                            postCandidateNote({
                                              cardKey,
                                              target: {
                                                type: 'candidate',
                                                candidateId,
                                                sentenceIds
                                              }
                                            })
                                          }
                                          onToggleActions={(
                                            noteId,
                                            anchorRect
                                          ) => {
                                            if (openActionsNoteId === noteId) {
                                              setOpenActionsNoteId(null);
                                              setOpenActionsAnchorRect(null);
                                              return;
                                            }

                                            setOpenActionsNoteId(noteId);
                                            setOpenActionsAnchorRect(
                                              anchorRect
                                            );
                                          }}
                                          onCloseActions={() => {
                                            setOpenActionsNoteId(null);
                                            setOpenActionsAnchorRect(null);
                                          }}
                                          onStartEdit={startEditingNote}
                                          onEditNoteChange={setEditNoteValue}
                                          onCancelEdit={cancelEditingNote}
                                          onSaveEdit={saveEditedNote}
                                          onDelete={deleteNote}
                                        />
                                      ) : null}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        );
                      }
                    )
                  : null}
              </SpaceBetween>
            ) : null}
          </SpaceBetween>
        </Container>
      </div>
      <ScrollDownArrow
        containerRef={containerRef as React.RefObject<HTMLElement>}
        bottomValue="7%"
      />
    </div>
  );
};

export default AnalyzedContractViewer;
